import { useEffect, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  Drawer,
  FormField,
  Input,
  StyledSelect,
  Textarea,
  type StyledSelectOption,
} from '@jumpstone/ui-react';
import { hoursDisplayToScaled } from '../../data/use_time_entries';
import type {
  CreateTimeEntryInput,
  CreateTimeEntryResult,
} from '../../data/use_time_entries';
import type { Employee } from '../../data/types';
import { create_time_entry_error_key } from './timesheet_utils';

/**
 * Matches the Data Centre's shared `StyledSelect` trigger shell so the
 * timesheets form shares its neighbours' input rhythm.
 */
const SELECT_TRIGGER_CLASSES =
  'nv-input flex h-9 w-full cursor-pointer items-center justify-between gap-2';

const MAX_DESCRIPTION_LENGTH = 2000;

export interface TimesheetCreateDrawerProps {
  open: boolean;
  onClose: () => void;
  employees: readonly Employee[];
  onSave: (input: CreateTimeEntryInput) => Promise<CreateTimeEntryResult>;
}

/**
 * Create-a-time-entry side drawer. Mirrors `DeductionDrawer` one-for-one so
 * the WagePro admin gets the same form rhythm across every catalog write —
 * inline validation for hours (must parse via `hoursDisplayToScaled`),
 * inline error rendering for a failed save (never a raw error toast).
 *
 * `hours` is transported as the integer scaled × 10,000 units the server
 * expects (see docs/money-rules.md § Quantities and
 * `hoursDisplayToScaled`'s docblock). The visible input still speaks
 * decimal hours ("7.5") so the boundary conversion is one place.
 */
export function TimesheetCreateDrawer({
  open,
  onClose,
  employees,
  onSave,
}: TimesheetCreateDrawerProps): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [employee_id, set_employee_id] = useState<string>('');
  const [work_date, set_work_date] = useState<string>('');
  const [hours_text, set_hours_text] = useState<string>('');
  const [description, set_description] = useState<string>('');
  const [saving, set_saving] = useState(false);
  const [save_failure, set_save_failure] = useState<
    'entitlement_required' | 'forbidden' | 'not_found' | 'error' | null
  >(null);
  const [dirty, set_dirty] = useState(false);

  useEffect(() => {
    if (open) {
      set_employee_id('');
      set_work_date('');
      set_hours_text('');
      set_description('');
      set_saving(false);
      set_save_failure(null);
      set_dirty(false);
    }
  }, [open]);

  const trimmed_hours = hours_text.trim();
  const scaled_hours =
    trimmed_hours.length === 0 ? null : hoursDisplayToScaled(hours_text);
  const hours_invalid = dirty && scaled_hours === null;
  const employee_missing = dirty && employee_id === '';
  const work_date_missing = dirty && work_date === '';
  const description_too_long = description.length > MAX_DESCRIPTION_LENGTH;

  const is_valid =
    employee_id !== '' &&
    work_date !== '' &&
    scaled_hours !== null &&
    !description_too_long;

  const employee_options: StyledSelectOption[] = employees.map((emp) => ({
    value: emp.id,
    label: emp.name,
  }));

  async function handle_save(): Promise<void> {
    set_dirty(true);
    if (!is_valid) return;
    set_saving(true);
    set_save_failure(null);
    const trimmed_desc = description.trim();
    const result = await onSave({
      employee_id,
      work_date,
      hours: scaled_hours,
      description: trimmed_desc.length > 0 ? trimmed_desc : undefined,
    });
    set_saving(false);
    if (result.outcome === 'success') {
      onClose();
      return;
    }
    set_save_failure(result.failure);
  }

  return (
    <Drawer open={open} onClose={onClose} title={t('timesheets.form.title')}>
      <div className="flex flex-col gap-3">
        <p className="text-[13px] text-[color:var(--ink-muted)]">
          {t('timesheets.form.description_form')}
        </p>

        <FormField
          label={t('timesheets.form.employee_label')}
          error={
            employee_missing
              ? t('timesheets.form.employee_required')
              : undefined
          }
        >
          <StyledSelect
            value={employee_id}
            onChange={(next) => {
              set_employee_id(next);
            }}
            options={employee_options}
            ariaLabel={t('timesheets.form.employee_label')}
            placeholder={t('timesheets.form.employee_placeholder')}
            triggerClassName={SELECT_TRIGGER_CLASSES}
          />
        </FormField>

        <FormField
          label={t('timesheets.form.work_date_label')}
          htmlFor="timesheet-work-date"
          error={
            work_date_missing
              ? t('timesheets.form.work_date_required')
              : undefined
          }
        >
          <Input
            id="timesheet-work-date"
            type="date"
            value={work_date}
            onChange={(event) => {
              set_work_date(event.target.value);
            }}
          />
        </FormField>

        <FormField
          label={t('timesheets.form.hours_label')}
          htmlFor="timesheet-hours"
          helpText={t('timesheets.form.hours_hint')}
          error={hours_invalid ? t('timesheets.form.hours_invalid') : undefined}
        >
          <Input
            id="timesheet-hours"
            type="text"
            inputMode="decimal"
            value={hours_text}
            onChange={(event) => {
              set_hours_text(event.target.value);
            }}
          />
        </FormField>

        <FormField
          label={t('timesheets.form.description_label')}
          htmlFor="timesheet-description"
          error={
            description_too_long
              ? t('timesheets.form.description_too_long', {
                  max: MAX_DESCRIPTION_LENGTH,
                })
              : undefined
          }
        >
          <Textarea
            id="timesheet-description"
            value={description}
            placeholder={t('timesheets.form.description_placeholder')}
            onChange={(event) => {
              set_description(event.target.value);
            }}
          />
        </FormField>

        {save_failure !== null && (
          <p className="nv-help is-error" role="alert">
            {t(create_time_entry_error_key(save_failure))}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <Button
            variant="primary"
            size="sm"
            disabled={saving}
            loading={saving}
            onClick={() => void handle_save()}
          >
            {t('timesheets.form.submit')}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
